import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster as ShadcnToaster } from './components/ui/toaster';
import { Toaster as HotToaster } from 'react-hot-toast';
import Navbar from './components/Layout/Navbar';
import Login from './pages/Auth/Login';
import EnhancedDashboard from './pages/OEM/EnhancedDashboard';
import InstallerJobs from './pages/Installer/Jobs';
import EnhancedAdminDashboard from './pages/Admin/EnhancedDashboard';
import EnhancedScheduler from './pages/Admin/EnhancedScheduler';
import Settings from './pages/Admin/Settings';
import EmailTemplateManagement from './pages/Admin/EmailTemplateManagement';
import UserManagement from './pages/Admin/UserManagement';
import CreateUser from './pages/Admin/CreateUser';
import EditUser from './pages/Admin/EditUser';
import ViewUser from './pages/Admin/ViewUser';
import LocationManagement from './pages/Admin/LocationManagement';
import Sv1ChecklistManagement from './pages/Admin/Sv1ChecklistManagement';
import Sv2ChecklistManagement from './pages/Admin/Sv2ChecklistManagement';
import ClientReviewChecklistManagement from './pages/Admin/ClientReviewChecklistManagement';
import HolidayManagement from './pages/Admin/HolidayManagement';
import EntityChecklistMaster from './pages/Admin/EntityChecklistMaster';
import CreateEntity from './pages/Admin/CreateEntity';
import CreateProject from './pages/Admin/CreateProject';
import SupervisorDashboard from './pages/Supervisor/Dashboard';
import ProjectViewEdit from './pages/ProjectViewEdit';
import ProjectDetailView from './pages/ProjectDetailView';
import EmployeeProfile from './pages/Employee/Profile';

// Static landing page redirect (serves /public/landing.html as home)
const LandingRedirect = () => {
  React.useEffect(() => {
    window.location.replace('/landing.html');
  }, []);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // DEBUG: Log the user role and allowed roles
  console.log('ProtectedRoute Check:', {
    user: user,
    userRole: user.role,
    allowedRoles: allowedRoles,
    roleMatch: allowedRoles ? allowedRoles.includes(user.role) : 'N/A'
  });

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    console.log('REDIRECTING to / - Role mismatch!');
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppContent() {
  return (
    <div className="App">
      <Navbar />
      <Routes>
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/login" element={<Login />} />

        {/* OEM Routes */}
        <Route
          path="/oem/dashboard"
          element={
            <ProtectedRoute allowedRoles={['oem']}>
              <EnhancedDashboard />
            </ProtectedRoute>
          }
        />

        {/* Installer / Aligner Routes */}
        <Route
          path="/installer/jobs"
          element={
            <ProtectedRoute allowedRoles={['installer', 'junioraligner']}>
              <InstallerJobs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/profile"
          element={
            <ProtectedRoute allowedRoles={['installer', 'junioraligner', 'supervisor']}>
              <EmployeeProfile />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <EnhancedAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/scheduler"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <EnhancedScheduler />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/users/create"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CreateUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/users/view/:id"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ViewUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/users/edit/:id"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <EditUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/locations"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <LocationManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/entity-checklist"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <EntityChecklistMaster />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/entity-checklist/create"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CreateEntity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/sv1-checklist"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Sv1ChecklistManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/sv2-checklist"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Sv2ChecklistManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/client-review-checklist"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ClientReviewChecklistManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/holidays"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <HolidayManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/email-templates"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <EmailTemplateManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/projects/create"
          element={
            <ProtectedRoute allowedRoles={['admin', 'oem']}>
              <CreateProject />
            </ProtectedRoute>
          }
        />

        {/* Supervisor Routes */}
        <Route
          path="/supervisor/dashboard"
          element={
            <ProtectedRoute allowedRoles={['supervisor']}>
              <SupervisorDashboard />
            </ProtectedRoute>
          }
        />

        {/* Project View/Edit - Accessible by admin, oem, supervisor, aligner */}
        <Route
          path="/projects/:id/view"
          element={
            <ProtectedRoute allowedRoles={['admin', 'oem', 'supervisor', 'aligner']}>
              <ProjectViewEdit />
            </ProtectedRoute>
          }
        />
      </Routes>
      <ShadcnToaster />
      <HotToaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;